import { Geolocation } from '@capacitor/geolocation'
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Navigation,
  Phone,
  Satellite,
  SatelliteDish,
  WifiOff,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { RouteMap } from '@/components/RouteMap'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import {
  formatDistance,
  formatDuration,
  useOptimizeRoute,
  useTodaysRoute,
  type RouteStop,
} from '@/features/field_routes/api'
import { openDirections } from '@/lib/maps'

/**
 * `/m/route` — the rep's day: the map, the stops in driving order, and one card at a time
 * to swipe through.
 *
 * The three states that are not "here is your round" get as much attention as the one that
 * is, because they are what a phone in a van actually does:
 *
 * - **No GPS.** The map still draws and the stops still list; only "where am I" is missing.
 *   A screen that refuses to show the round because it cannot find the rep is useless in
 *   exactly the basement car park where the rep needs to know which shop is next.
 * - **No network.** React Query keeps serving the last round it fetched, so a lost signal
 *   between stops leaves the list on screen with a banner rather than a spinner.
 * - **No route planned.** An empty state with a way out, not an error.
 */
export function MobileRoute() {
  const { data: route, isLoading, isError, refetch, isFetching } = useTodaysRoute()
  const optimize = useOptimizeRoute()

  const [fix, setFix] = useState<{ lat: number; lng: number; accuracy: number } | null>(null)
  const [gpsError, setGpsError] = useState<string | null>(null)
  const [online, setOnline] = useState(() => navigator.onLine)
  const [active, setActive] = useState(0)

  useEffect(() => {
    const update = () => setOnline(navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])

  const locate = useCallback(async () => {
    setGpsError(null)
    try {
      const permission = await Geolocation.requestPermissions()
      if (permission.location === 'denied') {
        setGpsError('Location permission is off. The route still works; your position is not shown.')
        return null
      }
      const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true })
      const next = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
      }
      setFix(next)
      return next
    } catch {
      setGpsError('GPS is unavailable right now. The route still works; your position is not shown.')
      return null
    }
  }, [])

  // One fix on open. Not a watch: a continuous position feed costs battery all day to
  // move a dot the rep is not looking at, and every screen that needs an accurate fix
  // (check-in, re-optimise) takes its own at the moment it needs it.
  useEffect(() => {
    void locate()
  }, [locate])

  const stops = route?.stops ?? []
  const activeStop = stops[active]

  const reoptimise = async () => {
    if (!route) return
    const here = await locate()
    await optimize.mutateAsync({
      routeId: route.id,
      start: here ? { lat: here.lat, lng: here.lng } : undefined,
    })
    setActive(0)
  }

  if (isLoading) {
    return <div className="p-4 text-sm text-slate-500">Loading your route…</div>
  }

  if (isError && !route) {
    return (
      <div className="space-y-3">
        <Alert tone="error">
          Could not load your route.{' '}
          {online ? 'The server did not answer.' : 'You are offline and it has not been opened on this phone yet.'}
        </Alert>
        <Button className="h-11 w-full" onClick={() => void refetch()} loading={isFetching}>
          Try again
        </Button>
      </div>
    )
  }

  if (!route) {
    return (
      <Card>
        <CardHeader title="No route today" description="Nothing has been planned for you." />
        <CardBody className="space-y-3">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Your manager plans the day&rsquo;s round. You can still record a walk-in visit or
            capture a new lead.
          </p>
          <Link
            to="/m/leads/new"
            className="block rounded-lg border border-slate-200 bg-white p-3 text-center text-sm font-medium text-brand-600 dark:border-slate-800 dark:bg-slate-900 dark:text-brand-400"
          >
            Capture a lead
          </Link>
        </CardBody>
      </Card>
    )
  }

  const completed = stops.filter((s) => s.status === 'completed').length

  return (
    <div className="space-y-4">
      {!online && (
        <Alert tone="info">
          <span className="flex items-center gap-2">
            <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
            No network. Showing the round as it was last loaded.
          </span>
        </Alert>
      )}

      <GpsStatus fix={fix} error={gpsError} onRetry={() => void locate()} />

      <RouteMap stops={stops} current={fix} activeIndex={active} onSelectStop={setActive} />

      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-900">
        <div>
          <p className="font-semibold text-slate-900 dark:text-slate-100">
            {completed} of {stops.length} done
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {formatDistance(route.total_distance_m)} · {formatDuration(route.total_duration_s)}
            {route.distance_provider === 'haversine' && ' (estimated)'}
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="h-11"
          onClick={() => void reoptimise()}
          loading={optimize.isPending}
          disabled={!online}
        >
          Re-plan from here
        </Button>
      </div>

      {activeStop && (
        <StopCard
          stop={activeStop}
          index={active}
          total={stops.length}
          onPrev={() => setActive((i) => Math.max(0, i - 1))}
          onNext={() => setActive((i) => Math.min(stops.length - 1, i + 1))}
        />
      )}

      <Card>
        <CardHeader title="All stops" description="In driving order." />
        <CardBody className="p-0">
          <ol className="divide-y divide-slate-100 dark:divide-slate-800">
            {stops.map((stop, index) => (
              <li key={stop.visit_id}>
                <button
                  type="button"
                  onClick={() => setActive(index)}
                  aria-current={index === active ? 'true' : undefined}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left ${
                    index === active ? 'bg-slate-50 dark:bg-slate-800/60' : ''
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${
                      stop.status === 'completed' ? 'bg-emerald-600' : 'bg-slate-400'
                    }`}
                  >
                    {stop.status === 'completed' ? (
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      index + 1
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                      {stop.customer_name}
                    </span>
                    <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                      {stop.customer_address ?? 'No address on file'}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-slate-500">
                    {formatDistance(stop.leg_distance_m)}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </CardBody>
      </Card>
    </div>
  )
}

/**
 * The GPS state, always visible rather than only when it breaks. A rep who can see the
 * accuracy figure knows whether a check-in is going to be accepted before they try it,
 * which is the difference between a retry and a support call.
 */
function GpsStatus({
  fix,
  error,
  onRetry,
}: {
  fix: { lat: number; lng: number; accuracy: number } | null
  error: string | null
  onRetry: () => void
}) {
  if (error) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
        data-testid="gps-status-error"
      >
        <Satellite className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1">{error}</span>
        <button type="button" onClick={onRetry} className="font-semibold underline">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div
      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"
      data-testid="gps-status-ok"
    >
      <SatelliteDish className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
      {fix ? (
        <span className="tabular-nums">
          GPS ready · ±{Math.round(fix.accuracy)} m
        </span>
      ) : (
        <span>Finding your position…</span>
      )}
    </div>
  )
}

/**
 * One stop at a time, swipeable.
 *
 * The swipe is handled with pointer events rather than a gesture library: it is one axis,
 * one threshold, and pulling in a dependency for it would cost more bytes on a 3G
 * connection than the whole screen. The arrow buttons do the same job for anyone who
 * cannot swipe — a rep wearing gloves, a screen reader, a cracked digitiser.
 */
function StopCard({
  stop,
  index,
  total,
  onPrev,
  onNext,
}: {
  stop: RouteStop
  index: number
  total: number
  onPrev: () => void
  onNext: () => void
}) {
  const startX = useRef<number | null>(null)

  const onPointerDown = (event: React.PointerEvent) => {
    startX.current = event.clientX
  }
  const onPointerUp = (event: React.PointerEvent) => {
    if (startX.current === null) return
    const delta = event.clientX - startX.current
    startX.current = null
    // 48px: far enough that a tap or a scroll does not trigger it, close enough for a
    // thumb on a phone held one-handed.
    if (delta <= -48) onNext()
    else if (delta >= 48) onPrev()
  }

  return (
    <Card>
      <div
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        className="touch-pan-y"
        data-testid="stop-card"
      >
        <CardHeader
          title={stop.customer_name}
          description={`Stop ${index + 1} of ${total}${
            stop.customer_address ? ` · ${stop.customer_address}` : ''
          }`}
        />
        <CardBody className="space-y-3">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>{stop.visit_number}</span>
            <span className="tabular-nums">
              {formatDistance(stop.leg_distance_m)} · {formatDuration(stop.leg_duration_s)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              className="h-11"
              onClick={() =>
                stop.latitude != null &&
                stop.longitude != null &&
                openDirections({ latitude: Number(stop.latitude), longitude: Number(stop.longitude) })
              }
              disabled={stop.latitude == null || stop.longitude == null}
            >
              <Navigation className="h-4 w-4" aria-hidden="true" />
              Navigate
            </Button>
            <Button
              variant="secondary"
              className="h-11"
              onClick={() => stop.customer_phone && window.open(`tel:${stop.customer_phone}`)}
              disabled={!stop.customer_phone}
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call
            </Button>
          </div>

          <Link
            to={`/m/visits/${stop.visit_id}`}
            className="block rounded-lg bg-brand-600 px-4 py-3 text-center text-sm font-semibold text-white"
          >
            {stop.status === 'completed' ? 'View visit' : 'Open visit'}
          </Link>

          <div className="flex items-center justify-between pt-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              onClick={onPrev}
              disabled={index === 0}
              aria-label="Previous stop"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </Button>
            <span className="text-xs text-slate-400">Swipe to move between stops</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              onClick={onNext}
              disabled={index === total - 1}
              aria-label="Next stop"
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </Button>
          </div>
        </CardBody>
      </div>
    </Card>
  )
}
